<template>
  <div class="prospect-funnel-report">
    <!-- Header: range picker + cohort summary -->
    <div class="flex justify-space-between align-items-center flex-wrap gap-sm m-b-md">
      <div>
        <h3 class="m-y-none font-heavy text-18">Cohort analysis</h3>
        <p class="text-14 text-gray-600 m-t-xxs m-b-none">
          <template v-if="cohortSize !== null">
            <strong>{{ cohortSize }}</strong>
            {{ cohortSize === 1 ? 'lead' : 'leads' }}
            created
            <span v-if="cohortStartDate && cohortEndDate">
              between {{ formatDate(cohortStartDate) }} and {{ formatDate(cohortEndDate) }}
            </span>
            — followed through every stage they reached, even after this window ended.
          </template>
          <template v-else>Loading…</template>
        </p>
      </div>

      <div style="position: relative;">
        <button type="button" class="btn btn-default prospect-range-trigger" @click="togglePopover">
          <i class="fa-solid fa-calendar-days m-r-xs text-gray-500"></i>
          <span class="text-gray-600">Date range:</span>
          <span class="font-semi m-l-xxs">{{ rangeLabel }}</span>
          <i class="fa-solid fa-caret-down m-l-xs text-gray-500"></i>
        </button>
        <div v-if="showPopover" class="log-contact-popover" @click.stop>
          <h5 class="m-t-none m-b-sm font-semi">Cohort window</h5>
          <div class="form-group m-b-sm">
            <select v-model="localRange" class="form-control input-sm" @change="onRangeSelectChange">
              <option v-for="opt in rangeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div v-if="localRange === 'custom'" class="form-group m-b-sm flex gap-x-xs align-items-center flex-wrap">
            <input v-model="localStart" type="date" class="form-control input-sm" style="width: auto;" />
            <span class="text-12 text-gray-500">to</span>
            <input v-model="localEnd" type="date" class="form-control input-sm" style="width: auto;" />
          </div>
          <div class="flex justify-space-between">
            <button type="button" class="btn btn-default btn-sm" @click="showPopover = false">Close</button>
            <button
              v-if="localRange === 'custom'"
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!localStart || !localEnd"
              @click="applyCustom"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Partial-history banner: surfaces when the picker's start predates the
         team's earliest audit row. The audit log only began on phase-1
         deploy, so cohorts older than that have synthesized rows from the
         backfill task and may under-report intermediate transitions. -->
    <div v-if="showPartialBanner" class="alert alert-warning m-b-md">
      <i class="fa-solid fa-circle-info m-r-xxs"></i>
      Detailed funnel data is only available from
      <strong>{{ formatDate(earliestDataDate) }}</strong>.
      Earlier transitions in this window may be missing or approximate.
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center p-y-lg">
      <i class="fa-solid fa-spinner fa-spin text-gray-500"></i>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger">{{ errorMessage }}</div>

    <!-- Empty state: no leads created in this window -->
    <div v-else-if="cohortSize === 0" class="text-center p-y-lg text-gray-500">
      <p class="m-b-none">No leads in this window.</p>
      <p class="text-12">Try widening the date range.</p>
    </div>

    <!-- Empty state: leads exist but none have recorded stage transitions.
         Happens for cohorts that pre-date the stage-change audit log (before
         the backfill task ran), so there are no edges to draw a Sankey from. -->
    <div v-else-if="!hasFunnelData" class="text-center p-y-lg text-gray-500">
      <p class="m-b-none">No stage transitions recorded for this cohort yet.</p>
      <p class="text-12">Funnel data appears once these leads move between stages.</p>
    </div>

    <!-- Content: three stacked sections -->
    <template v-else>
      <!-- 1. Conversion metrics — exact numbers, copy-pasteable -->
      <section class="m-b-xl">
        <div class="flex justify-space-between align-items-center m-b-sm">
          <h4 class="font-semi text-16 m-y-none">Conversion metrics</h4>
          <button type="button" class="btn btn-default btn-sm" @click="copyMetrics">
            <i class="m-r-xxs" :class="copied ? 'fa-solid fa-check text-green-600' : 'fa-solid fa-paste'"></i>
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>

        <div class="alert alert-info m-b-md">
          <i class="fa-solid fa-circle-info m-r-xxs"></i>
          <strong>Leads can enter at multiple points</strong> — e.g. an inquiry form vs. a direct
          trial signup — so stage counts won't always step down evenly. Each count is the distinct
          leads that actually reached that stage, not a running subtotal.
        </div>

        <table class="table table-condensed m-b-md">
          <thead>
            <tr>
              <th>Stage</th>
              <th class="text-right">Leads</th>
              <th class="text-right">% of cohort</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in stageRows" :key="row.key">
              <td>{{ row.label }}</td>
              <td class="text-right">{{ row.count }}</td>
              <td class="text-right text-gray-600">{{ formatPct(row.pct) }}</td>
            </tr>
          </tbody>
        </table>

        <table class="table table-condensed">
          <thead>
            <tr>
              <th>Conversion</th>
              <th class="text-right">Ratio</th>
              <th class="text-right">Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in ratioRows" :key="row.label">
              <td>
                {{ row.label }}
                <i
                  v-if="row.note"
                  class="fa-solid fa-circle-info text-gray-400 m-l-xxs cursor-pointer"
                  :title="row.note"
                ></i>
              </td>
              <td class="text-right text-gray-600">{{ row.num }} / {{ row.den }}</td>
              <td class="text-right font-semi">{{ formatPct(row.pct) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Build-your-own ratio: pick any two stages and read the rate. All
             counts are already loaded client-side, so this is pure division. -->
        <div class="flex align-items-center flex-wrap gap-x-xs gap-y-xs text-14">
          <span class="font-semi">Custom ratio:</span>
          <select v-model="customNum" class="form-control input-sm" style="width: auto;">
            <option v-for="opt in ratioStageOptions" :key="`n-${opt.key}`" :value="opt.key">{{ opt.label }}</option>
          </select>
          <span class="text-gray-500">÷</span>
          <select v-model="customDen" class="form-control input-sm" style="width: auto;">
            <option v-for="opt in ratioStageOptions" :key="`d-${opt.key}`" :value="opt.key">{{ opt.label }}</option>
          </select>
          <span class="text-gray-500">=</span>
          <span class="font-semi">{{ formatPct(customRate) }}</span>
          <span class="text-gray-500 text-12">({{ countFor(customNum) }} / {{ countFor(customDen) }})</span>
        </div>
      </section>

      <!-- 2. Funnel chart — the spine, at-a-glance health -->
      <section class="m-b-xl">
        <h4 class="font-semi text-16 m-t-none m-b-xxs">Conversion funnel</h4>
        <p class="text-12 text-gray-500 m-t-none m-b-sm">
          Distinct leads that reached each stage. Percentages are of the full cohort.
        </p>
        <div ref="funnelContainer" class="prospect-funnel-spine"></div>
      </section>

      <!-- 3. Sankey — full flow, where every lead went -->
      <section>
        <h4 class="font-semi text-16 m-t-none m-b-xxs">Stage-to-stage flow</h4>
        <p class="text-12 text-gray-500 m-t-none m-b-sm">
          Every recorded transition, including where leads entered and exited.
        </p>
        <div ref="chartContainer" class="prospect-funnel-chart"></div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import Highcharts from 'highcharts';
import 'highcharts/modules/sankey';
import 'highcharts/modules/funnel';

// Display + color map for each stage. Source nodes (the synthetic
// "where did this lead enter the pipeline" buckets) are listed first so
// they render at the left edge of the Sankey.
const STAGE_DISPLAY = {
  __source_inquiry__: { label: 'Inquiry submitted', color: '#829AB0' }, // $n4
  __source_trial__: { label: 'Direct trial signup', color: '#829AB0' },
  inquiry: { label: 'Interested', color: '#829AB0' },
  trial_scheduled: { label: 'Trial scheduled', color: '#64B1E8' }, // $b3
  trialing: { label: 'Trialing', color: '#4299D7' }, // $b4
  trial_complete: { label: 'Trial complete', color: '#1870AF' }, // $b6
  converted: { label: 'Converted', color: '#3DBD92' }, // $p4
  didnt_join: { label: "Didn't join", color: '#E66B6B' }, // $r3
  archived: { label: 'Archived', color: '#BCCCDC' }, // $n2
};

// Left-to-right column for each Sankey node. Highcharts otherwise positions
// nodes by graph depth, which scrambled the funnel order; pinning columns
// forces a clean funnel-ordered read. Terminal stages share the last column.
const SANKEY_COLUMN = {
  __source_inquiry__: 0,
  __source_trial__: 0,
  inquiry: 1,
  trial_scheduled: 2,
  trialing: 3,
  trial_complete: 4,
  converted: 5,
  didnt_join: 5,
  archived: 5,
};

// The funnel chart's spine, in order, plus the cohort total on top. Exits
// (didnt_join / archived) are intentionally excluded from the spine — they
// live in the conversion table instead.
const FUNNEL_SPINE = ['inquiry', 'trial_scheduled', 'trialing', 'trial_complete', 'converted'];

// Stages shown in the conversion-metrics count table (spine + exits).
const TABLE_STAGES = ['inquiry', 'trial_scheduled', 'trialing', 'trial_complete', 'converted', 'didnt_join', 'archived'];

export default {
  name: 'prospect-funnel-report',
  props: {
    funnelUrl: { type: String, required: true },
  },
  data() {
    return {
      // Monotonic request counter — guards against out-of-order responses
      // when the coach switches ranges quickly (an older request resolving
      // last must not overwrite the newer range's state/charts).
      requestSeq: 0,
      loading: false,
      error: false,
      errorMessage: '',

      // API response
      edges: [],
      stageCounts: {},
      cohortSize: null,
      cohortStartDate: '',
      cohortEndDate: '',
      earliestDataDate: null,

      // Picker state
      range: '90',
      startDate: '',
      endDate: '',

      // Popover local-state buffer so the user can edit without firing requests.
      showPopover: false,
      localRange: '90',
      localStart: '',
      localEnd: '',

      funnelChart: null,
      sankeyChart: null,
      copied: false,

      // Custom-ratio picker (defaults to overall conversion).
      customNum: 'converted',
      customDen: '__all__',
    };
  },
  computed: {
    rangeOptions() {
      return [
        { label: '30-Day', value: '30' },
        { label: '60-Day', value: '60' },
        { label: '90-Day', value: '90' },
        { label: '180-Day', value: '180' },
        { label: 'Custom', value: 'custom' },
      ];
    },
    rangeLabel() {
      if (this.range === 'custom' && this.startDate && this.endDate) {
        return `${this.formatDate(this.startDate)} – ${this.formatDate(this.endDate)}`;
      }
      return `Last ${this.range} days`;
    },
    // Show the partial-history banner when the picker's effective start
    // date predates the team's earliest audit row. Skip when the team has
    // no rows at all (handled by the empty state instead).
    showPartialBanner() {
      if (!this.earliestDataDate || !this.cohortStartDate) return false;
      return new Date(this.cohortStartDate) < new Date(this.earliestDataDate);
    },
    // Whether there's at least one non-zero edge to draw. Mirrors the
    // count > 0 filter in buildChartData so the Sankey branch is only used
    // when it would actually render something.
    hasFunnelData() {
      return this.edges.some((edge) => edge.count > 0);
    },
    // Per-stage count rows for the conversion table (spine + exits).
    stageRows() {
      const cohort = this.cohortSize || 0;
      const rows = [{
        key: '__all__',
        label: 'All leads',
        count: cohort,
        pct: 100,
      }];
      TABLE_STAGES.forEach((key) => {
        const count = this.stageCounts[key] || 0;
        rows.push({
          key,
          label: STAGE_DISPLAY[key].label,
          count,
          pct: cohort > 0 ? (count / cohort) * 100 : null,
        });
      });
      return rows;
    },
    // Fixed set of consultant-style conversion ratios. Each is a literal
    // numerator/denominator over the per-stage reach counts so the rates
    // stay true to operations (skipped stages show as gaps, not smoothed).
    ratioRows() {
      const c = (key) => this.stageCounts[key] || 0;
      const cohort = this.cohortSize || 0;
      const defs = [
        {
          label: 'Converted ÷ All leads',
          num: c('converted'),
          den: cohort,
          note: 'Of the leads created in this window, how many ever converted. '
            + 'This is a cohort rate and differs from the conversion rate in the page '
            + 'header, which compares conversions during this period to new leads created '
            + 'during it (and can exceed 100%).',
        },
        { label: 'Trial scheduled ÷ Interested', num: c('trial_scheduled'), den: c('inquiry') },
        { label: 'Trialing ÷ Trial scheduled', num: c('trialing'), den: c('trial_scheduled') },
        { label: 'Trial complete ÷ Trialing', num: c('trial_complete'), den: c('trialing') },
        { label: 'Converted ÷ Trial complete', num: c('converted'), den: c('trial_complete') },
        { label: 'Converted ÷ Trial scheduled', num: c('converted'), den: c('trial_scheduled') },
      ];
      return defs.map((d) => ({
        ...d,
        pct: d.den > 0 ? (d.num / d.den) * 100 : null,
      }));
    },
    // Stage choices for the custom-ratio dropdowns: cohort total + every stage.
    ratioStageOptions() {
      return [{ key: '__all__', label: 'All leads' }]
        .concat(TABLE_STAGES.map((key) => ({ key, label: STAGE_DISPLAY[key].label })));
    },
    customRate() {
      const den = this.countFor(this.customDen);
      return den > 0 ? (this.countFor(this.customNum) / den) * 100 : null;
    },
  },
  mounted() {
    this.loadFunnel();
  },
  beforeDestroy() {
    if (this.funnelChart) this.funnelChart.destroy();
    if (this.sankeyChart) this.sankeyChart.destroy();
  },
  methods: {
    formatDate(value) {
      if (!value) return '';
      const d = new Date(value);
      // Date-only inputs (YYYY-MM-DD) parse as midnight UTC, which shifts
      // a day west of the user's timezone. Reading the UTC parts keeps the
      // displayed date matching what the picker shows.
      return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
    },
    // Lead count for a ratio-picker key ('__all__' = full cohort).
    countFor(key) {
      if (key === '__all__') return this.cohortSize || 0;
      return this.stageCounts[key] || 0;
    },
    // Whole number when clean, one decimal otherwise; em dash for n/a.
    formatPct(pct) {
      if (pct === null || pct === undefined) return '—';
      const rounded = Math.round(pct * 10) / 10;
      return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
    },
    togglePopover() {
      this.showPopover = !this.showPopover;
      if (this.showPopover) {
        this.localRange = this.range;
        this.localStart = this.startDate;
        this.localEnd = this.endDate;
      }
    },
    // Selecting a preset range applies immediately; switching to "custom"
    // waits for the user to fill in dates and click Apply, mirroring the
    // stats strip's popover UX so the controls feel identical.
    onRangeSelectChange() {
      if (this.localRange !== 'custom') {
        this.range = this.localRange;
        this.startDate = '';
        this.endDate = '';
        this.showPopover = false;
        this.loadFunnel();
      }
    },
    applyCustom() {
      this.range = 'custom';
      this.startDate = this.localStart;
      this.endDate = this.localEnd;
      this.showPopover = false;
      this.loadFunnel();
    },
    loadFunnel() {
      this.requestSeq += 1;
      const { requestSeq } = this;
      this.loading = true;
      this.error = false;
      const separator = this.funnelUrl.indexOf('?') !== -1 ? '&' : '?';
      let url;
      if (this.range === 'custom' && this.startDate && this.endDate) {
        url = `${this.funnelUrl + separator}start_date=${this.startDate}&end_date=${this.endDate}`;
      } else {
        const days = this.range === 'custom' ? 90 : this.range;
        url = `${this.funnelUrl + separator}days=${days}`;
      }
      axios.get(url)
        .then((response) => {
          if (requestSeq !== this.requestSeq) return;
          this.loading = false;
          this.edges = response.data.edges || [];
          this.stageCounts = response.data.stage_counts || {};
          this.cohortSize = response.data.cohort_size;
          this.cohortStartDate = response.data.cohort_start_date || '';
          this.cohortEndDate = response.data.cohort_end_date || '';
          this.earliestDataDate = response.data.earliest_data_date || null;
          this.$nextTick(() => this.renderCharts());
        })
        .catch((err) => {
          if (requestSeq !== this.requestSeq) return;
          this.loading = false;
          this.error = true;
          this.errorMessage = (err.response && err.response.data && err.response.data.error)
            || 'Could not load funnel data. Please try again.';
        });
    },
    renderCharts() {
      this.renderFunnel();
      this.renderSankey();
    },
    // Funnel chart of the spine. Built from the literal per-stage reach
    // counts so a stage that leads skip shows up smaller than the one below
    // it — surfacing the gap rather than masking it.
    renderFunnel() {
      if (!this.$refs.funnelContainer) return;
      if (this.funnelChart) {
        this.funnelChart.destroy();
        this.funnelChart = null;
      }
      const cohort = this.cohortSize || 0;
      const data = [{
        name: 'All leads',
        y: cohort,
        pct: '100',
        color: '#627D98',
      }];
      FUNNEL_SPINE.forEach((key) => {
        const count = this.stageCounts[key] || 0;
        data.push({
          name: STAGE_DISPLAY[key].label,
          y: count,
          pct: cohort > 0 ? (Math.round((count / cohort) * 1000) / 10).toString() : '0',
          color: STAGE_DISPLAY[key].color,
        });
      });

      this.funnelChart = Highcharts.chart(this.$refs.funnelContainer, {
        chart: { type: 'funnel', height: 360 },
        title: { text: null },
        credits: { enabled: false },
        accessibility: { enabled: false },
        tooltip: {
          headerFormat: '',
          pointFormat: '<b>{point.name}</b><br/>{point.y} leads ({point.pct}% of cohort)',
        },
        plotOptions: {
          series: {
            neckWidth: '30%',
            neckHeight: '0%',
            width: '70%',
            center: ['40%', '50%'],
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.y} ({point.pct}%)',
              softConnector: true,
              style: { fontWeight: 'normal', fontSize: '12px', textOutline: 'none' },
            },
          },
        },
        series: [{
          name: 'Leads',
          data,
        }],
      });
    },
    // Translate the API's flat edge list into Highcharts Sankey data.
    // The synthetic "source" nodes split the null→X bootstrap rows by
    // their first real stage so owners can see at a glance how many leads
    // came in via inquiry form vs. direct trial registration.
    buildSankeyData() {
      const links = [];
      const usedStages = new Set();

      this.edges.forEach((edge) => {
        if (edge.count === 0) return;
        let fromKey = edge.from;
        if (fromKey === null) {
          fromKey = edge.to === 'trial_scheduled' ? '__source_trial__' : '__source_inquiry__';
        }
        const toKey = edge.to;
        usedStages.add(fromKey);
        usedStages.add(toKey);
        links.push([STAGE_DISPLAY[fromKey].label, STAGE_DISPLAY[toKey].label, edge.count]);
      });

      const nodes = Array.from(usedStages).map((key) => ({
        id: STAGE_DISPLAY[key].label,
        color: STAGE_DISPLAY[key].color,
        column: SANKEY_COLUMN[key],
      }));

      return { links, nodes };
    },
    renderSankey() {
      if (!this.$refs.chartContainer) return;
      if (this.sankeyChart) {
        this.sankeyChart.destroy();
        this.sankeyChart = null;
      }
      const { links, nodes } = this.buildSankeyData();
      if (links.length === 0) return;

      this.sankeyChart = Highcharts.chart(this.$refs.chartContainer, {
        chart: { type: 'sankey', height: 480 },
        title: { text: null },
        credits: { enabled: false },
        accessibility: { enabled: false },
        tooltip: {
          headerFormat: '',
          pointFormat: '<b>{point.fromNode.name}</b> → <b>{point.toNode.name}</b><br/>{point.weight} leads',
          nodeFormat: '<b>{point.name}</b>: {point.sum} leads',
        },
        plotOptions: {
          sankey: {
            nodeWidth: 18,
            nodePadding: 12,
            curveFactor: 0.5,
            linkOpacity: 0.6,
            dataLabels: {
              style: { fontWeight: 'normal', fontSize: '12px', textOutline: 'none' },
            },
          },
        },
        series: [{
          type: 'sankey',
          name: 'Funnel',
          keys: ['from', 'to', 'weight'],
          data: links,
          nodes,
        }],
      });
    },
    // Copy both tables as TSV so they paste cleanly into Sheets/Excel/email.
    copyMetrics() {
      const lines = [];
      const win = (this.cohortStartDate && this.cohortEndDate)
        ? `${this.formatDate(this.cohortStartDate)}–${this.formatDate(this.cohortEndDate)}`
        : '';
      lines.push(`Cohort analysis${win ? ` (${win})` : ''}`);
      lines.push('');
      lines.push(['Stage', 'Leads', '% of cohort'].join('\t'));
      this.stageRows.forEach((r) => {
        lines.push([r.label, r.count, this.formatPct(r.pct)].join('\t'));
      });
      lines.push('');
      lines.push(['Conversion', 'Ratio', 'Rate'].join('\t'));
      this.ratioRows.forEach((r) => {
        lines.push([r.label, `${r.num} / ${r.den}`, this.formatPct(r.pct)].join('\t'));
      });
      const text = lines.join('\n');

      const done = () => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); done(); } finally { document.body.removeChild(ta); }
    },
  },
};
</script>
